import { payDateListInMonth } from '../engine/cashPolicy';
import { monthKeyFromT } from '../engine/calendar';
import { listEligibleIgnoreGroups } from '../engine/ignoreGroups';
import type { History, RecipeDefaults, SchemaEntry, View } from '../engine/types';
import { PICKER_DRIVER_ORDER, type PickerDriver } from '../gridClasses';
import { driverHelpText, driverInputUnit } from '../driverUtils';
import { seasonalityFromHistory } from './shape';
import type { CaseFileHistorySummary, CaseFileV1 } from './types';
import {
    blankCashPolicy,
    buildCaseFileLine,
    buildReferenceBlock,
    collectTemplateLineKeys,
    emptyLinesRecord,
    fileMonthFromLastClosedDate,
    ignoreGroupInputsStub,
    lastClosedDateFromFileMonth,
    projectionMonthKeys,
} from './utils';

function appliesToHint(driver: PickerDriver): string {
    if (driver === 'carry_forward' || driver === 'ignore') {
        return driver;
    }
    const unit = driverInputUnit(driver);
    if (unit === 'pct') {
        return 'pct';
    }
    if (['dio', 'dso', 'dpo'].includes(driver)) {
        return 'days';
    }
    if (unit === 'count') {
        return driver.includes('per') ? 'count/rate' : 'count';
    }
    if (driver.includes('per') || driver.includes('sq') || driver.includes('fte')) {
        return '$/rate';
    }
    if (driver.includes('dollar') || driver === 's_curve') {
        return '$';
    }
    return driver;
}

export function buildGuide(schema: SchemaEntry[]): Record<string, unknown> {
    const drivers: Record<
        string,
        { applies_to: string; input_unit: string; formula_in_words: string }
    > = {};
    for (const driver of PICKER_DRIVER_ORDER) {
        drivers[driver] = {
            applies_to: appliesToHint(driver),
            input_unit: driverInputUnit(driver),
            formula_in_words: driverHelpText(driver),
        };
    }
    const ignoreGroups: Record<string, string[]> = {};
    for (const group of listEligibleIgnoreGroups(schema)) {
        ignoreGroups[group.parent] = [...group.members];
    }
    return {
        about:
            'Bookkeeper projection case file. One driver per line; omitted or null driver = undriven. Omitted/null driver on a BS input line is implicit carry-forward.',
        percent_convention:
            'All percent drivers use decimal fractions (5% = 0.05, not 5).',
        percent_convention_reminder: 'Percent convention: decimal fractions only — 5% is 0.05.',
        values_forms: {
            array: '36 numbers/nulls, index 0 = first projection month (M+1)',
            constant: 'single number applies to all 36 months',
            step_map:
                '{"2026-09": 100, "2026-12": 120} — forward-filled; months before first key stay null',
        },
        drivers,
        fixed_lines_omitted:
            'GPS, Total Orders, Days Open for Business, Store Status, cash plug, linked accum dep/amort, Plus Net Income, carry-forward-only BS lines omitted.',
        ignore_groups: ignoreGroups,
        ignore_rule: 'Drive a group parent only when every member in that view is set to ignore.',
        consolidated_only:
            'List IS line keys in consolidated_only and drive them only under lines.consolidated.',
        one_time_items:
            '{ month, view, line_key, amount, mode: incremental|already_in_driver, notes?, id? }',
        notes: '{view}:{line_key}:{1..36|label}. Empty string deletes on upload.',
        save_warnings:
            'Undriven IS lines with activity in the last 12 months are reported when the case is saved. Undriven BS input lines carry forward implicitly and are not reported.',
        cash_policy:
            "While enabled, Cash – Equivalents and distribution lines are owned by the policy. Scott McClelland's vested Class B share is accrued to Distributions Payable when a real distribution is logged; projections treat the gross amount as cash and hold Distributions Payable flat. Optional distribution.one_time models a projected extra payout (not logged on Waterfall).",
        labor_buildup:
            'Two BOH hires in March: step map on is.hourly_fte_boh, e.g. {"2026-03": 2}. 3% raise in January: step or growth on is.avg_hourly_rate_boh.',
        recipe_form:
            'Recipe form (format_version 2): {"anchor":{"basis":"trailing_avg","months":3,"value":0.263},"path":[{"to":0.2593,"by":"2027-02"}],"rationale":"..."} on is.cogs_food with pct_gross_sales; or {"anchor":{"basis":"last_actual_per_period","months":3,"value":60500},"growth":{"per_month":0.0035},"seasonality":{"factors":{"11":1.05,"12":1.12}},"rationale":"..."} on is.payroll_wages. Supported from format_version 2; until then write arrays or step maps and put the rationale in a label note.',
        recipe_authoring:
            'State an anchor with a basis for every line that is not flat, and a rationale of ≤ 60 words that names the baseline period and the reason for any path or growth.',
        prefer_meaningful_drivers:
            'Prefer drivers that carry meaning: dollar_per_pay_period for wages, pct_gross_sales for variable costs, count_per_open_day for orders.',
        overrides_are_sparse:
            'Overrides are for a few specific months, not a way to type 36 numbers.',
    };
}

const GPS_KEY = 'is.gross_product_sales';
const DAYS_OPEN_KEY = 'is.days_open_for_business';
const VARIABLE_OPEX_KEYS = new Set(['is.advertising_and_marketing_expense']);

function historyValueAt(
    history: History,
    view: View,
    lineKey: string,
    t: number,
): number | null {
    const raw = history.values[view]?.[lineKey]?.[history.nHistory - 1 + t] ?? null;
    if (raw == null) {
        return null;
    }
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : null;
}
const SEASONALITY_LINES = [
    'is.avg_orders_per_day',
    'is.avg_ticket',
    'is.gross_product_sales',
] as const;
const LOCATION_VIEWS: View[] = ['river_oaks', 'highland_park'];

function historyByMonth(
    history: History,
    view: View,
    lineKey: string,
    lastClosedDate: string,
): Record<string, number | null> {
    const series = history.values[view]?.[lineKey] ?? [];
    const out: Record<string, number | null> = {};
    for (let idx = 0; idx < series.length; idx += 1) {
        const t = idx - (history.nHistory - 1);
        const mk = monthKeyFromT(lastClosedDate, t);
        const raw = series[idx];
        out[mk] = typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
    }
    return out;
}

function chooseHistoryUnit(
    schema: SchemaEntry[],
    lineKey: string,
    recipeDefaults?: RecipeDefaults,
): string {
    if (lineKey === 'is.avg_orders_per_day') {
        return 'count_per_open_day';
    }
    if (lineKey === 'is.avg_ticket') {
        return 'dollar_value';
    }
    if (
        lineKey === 'is.payroll_wages' &&
        recipeDefaults?.default_pay_cadence &&
        recipeDefaults.default_pay_anchor_date
    ) {
        return 'dollar_per_pay_period';
    }
    const entry = schema.find((row) => row.key === lineKey);
    if (!entry) {
        return 'dollar_value';
    }
    if (entry.tab === 'BS') {
        return 'balance';
    }
    const gpRow = schema.find((row) => row.key === 'is.gross_profit')?.row;
    if (
        entry.unit === 'usd' &&
        entry.row != null &&
        gpRow != null &&
        (entry.row < gpRow || VARIABLE_OPEX_KEYS.has(lineKey))
    ) {
        return 'pct_gross_sales';
    }
    return 'dollar_value';
}

function convertToUnit(
    unit: string,
    lineKey: string,
    view: View,
    history: History,
    lastClosedDate: string,
    t: number,
    recipeDefaults?: RecipeDefaults,
): number | null {
    const usd = historyValueAt(history, view, lineKey, t);
    if (usd == null) {
        return null;
    }
    if (unit === 'balance' || unit === 'dollar_value') {
        return usd;
    }
    if (unit === 'count_per_open_day') {
        if (lineKey === 'is.avg_orders_per_day') {
            return usd;
        }
        const daysOpen = historyValueAt(history, view, DAYS_OPEN_KEY, t);
        if (daysOpen == null || daysOpen === 0) {
            return null;
        }
        return usd / daysOpen;
    }
    if (unit === 'pct_gross_sales') {
        const gps = historyValueAt(history, view, GPS_KEY, t);
        if (gps == null || gps === 0) {
            return null;
        }
        return usd / gps;
    }
    if (unit === 'dollar_per_pay_period') {
        const cadence = recipeDefaults?.default_pay_cadence ?? 'biweekly';
        const anchor = recipeDefaults?.default_pay_anchor_date;
        if (!anchor) {
            return usd;
        }
        const mk = monthKeyFromT(lastClosedDate, t);
        const n = payDateListInMonth(cadence, anchor, mk).length;
        if (n === 0) {
            return null;
        }
        return usd / n;
    }
    return usd;
}

function trailingAvgInUnit(
    unit: string,
    lineKey: string,
    view: View,
    history: History,
    lastClosedDate: string,
    months: number,
    recipeDefaults?: RecipeDefaults,
): number | null {
    if (unit === 'pct_gross_sales') {
        let num = 0;
        let den = 0;
        for (let t = -(months - 1); t <= 0; t += 1) {
            const line = historyValueAt(history, view, lineKey, t);
            const gps = historyValueAt(history, view, GPS_KEY, t);
            if (line == null || gps == null || gps === 0) {
                continue;
            }
            num += line;
            den += gps;
        }
        return den === 0 ? null : num / den;
    }
    if (unit === 'count_per_open_day') {
        let num = 0;
        let den = 0;
        for (let t = -(months - 1); t <= 0; t += 1) {
            const perDay = historyValueAt(history, view, lineKey, t);
            const daysOpen = historyValueAt(history, view, DAYS_OPEN_KEY, t);
            if (perDay == null || daysOpen == null || daysOpen === 0) {
                continue;
            }
            if (lineKey === 'is.avg_orders_per_day') {
                num += perDay * daysOpen;
            } else {
                num += perDay;
            }
            den += daysOpen;
        }
        return den === 0 ? null : num / den;
    }
    if (unit === 'dollar_per_pay_period') {
        const cadence = recipeDefaults?.default_pay_cadence ?? 'biweekly';
        const anchor = recipeDefaults?.default_pay_anchor_date;
        if (!anchor) {
            return null;
        }
        let num = 0;
        let den = 0;
        for (let t = -(months - 1); t <= 0; t += 1) {
            const line = historyValueAt(history, view, lineKey, t);
            const mk = monthKeyFromT(lastClosedDate, t);
            const n = payDateListInMonth(cadence, anchor, mk).length;
            if (line == null || n === 0) {
                continue;
            }
            num += line;
            den += n;
        }
        return den === 0 ? null : num / den;
    }
    const vals: number[] = [];
    for (let t = -(months - 1); t <= 0; t += 1) {
        const v = convertToUnit(unit, lineKey, view, history, lastClosedDate, t, recipeDefaults);
        if (v != null) {
            vals.push(v);
        }
    }
    if (vals.length === 0) {
        return null;
    }
    return vals.reduce((s, v) => s + v, 0) / vals.length;
}

function buildHistorySummary(
    schema: SchemaEntry[],
    lineKey: string,
    view: View,
    history: History,
    lastClosedDate: string,
    recipeDefaults?: RecipeDefaults,
): CaseFileHistorySummary {
    const unit = chooseHistoryUnit(schema, lineKey, recipeDefaults);
    const lastVal = convertToUnit(unit, lineKey, view, history, lastClosedDate, 0, recipeDefaults);
    const lastUsd = history.values[view]?.[lineKey]?.[history.nHistory - 1] ?? null;
    const sameMonthLastYear: Record<string, number> = {};
    for (let t = 1; t <= 12; t += 1) {
        const mk = monthKeyFromT(lastClosedDate, t);
        const smly = convertToUnit(unit, lineKey, view, history, lastClosedDate, t - 12, recipeDefaults);
        if (smly != null) {
            sameMonthLastYear[mk] = smly;
        }
    }
    const last12: number[] = [];
    for (let t = -11; t <= 0; t += 1) {
        const v = convertToUnit(unit, lineKey, view, history, lastClosedDate, t, recipeDefaults);
        if (v != null) {
            last12.push(v);
        }
    }
    const min = last12.length > 0 ? Math.min(...last12) : 0;
    const max = last12.length > 0 ? Math.max(...last12) : 0;
    const summary: CaseFileHistorySummary = {
        unit,
        last_actual: lastVal != null
            ? {
                  month: monthKeyFromT(lastClosedDate, 0),
                  value: lastVal,
                  ...(typeof lastUsd === 'number' && Number.isFinite(lastUsd) ? { usd: lastUsd } : {}),
              }
            : null,
        trailing_avg: {
            '3': trailingAvgInUnit(unit, lineKey, view, history, lastClosedDate, 3, recipeDefaults) ?? 0,
            '6': trailingAvgInUnit(unit, lineKey, view, history, lastClosedDate, 6, recipeDefaults) ?? 0,
            '12': trailingAvgInUnit(unit, lineKey, view, history, lastClosedDate, 12, recipeDefaults) ?? 0,
        },
        same_month_last_year: sameMonthLastYear,
        min_max_12: { min, max },
    };
    return summary;
}

function attachSeasonalityReference(
    file: CaseFileV1,
    schema: SchemaEntry[],
    history: History,
    lastClosedDate: string,
): void {
    if (!file._reference) {
        file._reference = {};
    }
    const seasonality: Record<string, Record<string, Record<string, number>>> = {};
    const notes: Record<string, string> = {};
    for (const view of LOCATION_VIEWS) {
        seasonality[view] = {};
        let viewSkipped = false;
        for (const lineKey of SEASONALITY_LINES) {
            const byMonth = historyByMonth(history, view, lineKey, lastClosedDate);
            const unit = chooseHistoryUnit(schema, lineKey);
            let series = byMonth;
            if (unit === 'count_per_open_day' && lineKey !== 'is.avg_orders_per_day') {
                series = {};
                for (const [mk, val] of Object.entries(byMonth)) {
                    let t = 0;
                    for (let ti = -(history.nHistory - 1); ti <= 0; ti += 1) {
                        if (monthKeyFromT(lastClosedDate, ti) === mk) {
                            t = ti;
                            break;
                        }
                    }
                    const daysOpen = historyValueAt(history, view, DAYS_OPEN_KEY, t);
                    const numVal = typeof val === 'number' ? val : Number(val);
                    series[mk] =
                        Number.isFinite(numVal) && daysOpen != null && daysOpen !== 0
                            ? numVal / daysOpen
                            : null;
                }
            }
            const result = seasonalityFromHistory({ valuesByMonth: series, years: 3 });
            if (result.factors) {
                seasonality[view][lineKey] = result.factors;
            } else if (result.skipped_reason) {
                viewSkipped = true;
            }
        }
        if (viewSkipped && !notes[view]) {
            notes[view] = 'No complete calendar year of actuals';
        }
    }
    file._reference.seasonality = seasonality;
    if (Object.keys(notes).length > 0) {
        file._reference.seasonality_notes = notes;
    }
}

export interface BuildTemplateOptions {
    schema: SchemaEntry[];
    lastClosedMonth: string;
    schemaFingerprint: string;
    history?: History;
    historyMonths?: number;
    recipeDefaults?: RecipeDefaults;
}

export function buildTemplate(options: BuildTemplateOptions): CaseFileV1 {
    const { schema, lastClosedMonth, schemaFingerprint, history, historyMonths = 24, recipeDefaults } =
        options;
    const lastClosedDate = lastClosedMonth.includes('-') && lastClosedMonth.length === 7
        ? lastClosedDateFromFileMonth(lastClosedMonth)
        : lastClosedMonth;
    const fileMonth = lastClosedMonth.length === 7
        ? lastClosedMonth
        : fileMonthFromLastClosedDate(lastClosedDate);
    const groups = listEligibleIgnoreGroups(schema);
    const lineSpecs = collectTemplateLineKeys(schema, groups);
    const lines = emptyLinesRecord();

    for (const spec of lineSpecs) {
        const inputsStub = spec.ignoreMembers
            ? ignoreGroupInputsStub(
                  { parent: spec.lineKey, members: spec.ignoreMembers },
                  spec.view,
              )
            : undefined;
        lines[spec.view][spec.lineKey] = buildCaseFileLine(
            schema,
            spec.lineKey,
            spec.view,
            [],
            inputsStub,
            spec.ignoreMembers,
            history,
            historyMonths,
            lastClosedDate,
        );
    }

    const file: CaseFileV1 = {
        format: 'bookkeeper.projection_case',
        format_version: 1,
        _guide: buildGuide(schema),
        case: { name: '', description: '', expires_on: null },
        basis: {
            last_closed_month: fileMonth,
            months: projectionMonthKeys(lastClosedDate),
            schema_fingerprint: schemaFingerprint,
        },
        consolidated_only: [],
        lines,
        one_time_items: [],
        cash_policy: blankCashPolicy(),
        notes: {},
    };

    if (history) {
        file._reference = buildReferenceBlock(schema, history, historyMonths, lastClosedDate);
        attachSeasonalityReference(file, schema, history, lastClosedDate);
        for (const view of Object.keys(lines) as View[]) {
            for (const [lineKey, line] of Object.entries(lines[view])) {
                line.history_summary = buildHistorySummary(
                    schema,
                    lineKey,
                    view,
                    history,
                    lastClosedDate,
                    recipeDefaults,
                );
            }
        }
    }

    return file;
}
